import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  SpinnerGap,
  CheckCircle,
  XCircle,
  Phone,
  Envelope,
  MapPin,
  Receipt,
  Clock,
  Warning,
  X,
  FilePdf,
  CreditCard
} from "phosphor-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { loadFinixScript, initializeFinixForm } from "../lib/finixFlex";
import { getFinixFraudSessionId } from "../utils/finixFraudSession";
// Removed: PoweredByFSPCard import - branding footer not shown on payment pages

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PublicEstimate() {
  const { publicToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [estimate, setEstimate] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [declined, setDeclined] = useState(false);
  
  // Deposit payment state
  const [showPayDepositDialog, setShowPayDepositDialog] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [finixReady, setFinixReady] = useState(false);
  const finixFormRef = useRef(null);
  const fraudSessionIdRef = useRef(null);

  useEffect(() => {
    fetchEstimate();
  }, [publicToken]);

  const fetchEstimate = async () => {
    try {
      const response = await fetch(`${API}/public/estimate/${publicToken}`);
      
      if (!response.ok) {
        const data = await response.json();
        setError(data.detail || "Unable to load estimate");
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      setEstimate(data);
      // Check for both "approved" (new) and "accepted" (legacy) to indicate customer accepted
      setAccepted(data.status === "approved" || data.status === "accepted" || !!data.accepted_at);
      setDeclined(data.status === "declined");
    } catch (err) {
      setError("Unable to load estimate. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const response = await fetch(`${API}/public/estimate/${publicToken}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || "Failed to accept estimate");
        setAccepting(false);
        return;
      }
      
      // Re-fetch estimate to get latest state including deposit fields
      await fetchEstimate();
      
      setAccepted(true);
      setShowAcceptDialog(false);
      toast.success("Estimate accepted successfully!");
    } catch (err) {
      toast.error("Failed to accept estimate. Please try again.");
    } finally {
      setAccepting(false);
    }
  };

  // ==========================================================================
  // DEPOSIT PAYMENT FUNCTIONS
  // ==========================================================================
  
  // Initialize Finix PaymentForm when dialog opens
  useEffect(() => {
    if (!showPayDepositDialog || !paymentConfig?.ok) return;
    
    let mounted = true;
    let initAttempted = false;
    
    const initFinix = async () => {
      if (initAttempted) return;
      initAttempted = true;
      
      try {
        // Load SDK using the shared library
        await loadFinixScript();
        
        // Wait longer for container to be fully in DOM and dialog animation to complete
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (!mounted) return;
        
        const isDebug = localStorage.getItem('FSP_FRAUD_DEBUG') === '1';
        
        // Start fraud session collection in parallel (best-effort, non-blocking)
        // Uses application_id or merchant_id from paymentConfig
        const env = paymentConfig.environment;
        const appId = paymentConfig.application_id;
        const merchId = paymentConfig.merchant_id;
        
        // Per Finix docs: merchant_id is REQUIRED for fraud detection
        // Only attempt fraud session if we have merchantId
        if (merchId) {
          if (isDebug) {
            console.info('[FSP_FRAUD_DEBUG] Starting fraud session collection:', {
              env,
              merchId: merchId ? `***${merchId.slice(-4)}` : null
            });
          }
          getFinixFraudSessionId({
            environment: env,
            merchantId: merchId,
            timeoutMs: 3000
          }).then(sessionId => {
            if (mounted) {
              fraudSessionIdRef.current = sessionId;
              if (isDebug) {
                console.info('[FSP_FRAUD_DEBUG] Fraud session stored:', { length: sessionId?.length || 0 });
              }
            }
          });
        } else if (isDebug) {
          console.info('[FSP_FRAUD_DEBUG] No merchantId available - skipping fraud session');
        }
        
        const container = document.getElementById("public-deposit-finix-form");
        if (!container) {
          console.error("[PublicEstimate] Container not found");
          return;
        }
        
        // Clear any existing content
        container.innerHTML = "";
        
        // Initialize using the shared library
        // When user clicks Submit in Finix form, onTokenize fires automatically
        const form = initializeFinixForm("public-deposit-finix-form", {
          environment: paymentConfig.environment,
          applicationId: paymentConfig.application_id,
          onTokenize: async (data) => {
            const isDebugSubmit = localStorage.getItem('FSP_FRAUD_DEBUG') === '1';
            
            if (isDebugSubmit) {
              console.info('[FSP_FRAUD_DEBUG] onTokenize called, token present:', !!data?.tokenId);
            }
            
            if (!mounted) return;
            
            // Extract and validate token
            const tokenId = data?.tokenId;
            
            // CRITICAL: Validate token format before calling backend
            // Token must be TK followed by at least 10 alphanumeric chars
            const TOKEN_REGEX = /^TK[a-zA-Z0-9]{10,}$/;
            
            if (!tokenId) {
              console.error("[PublicEstimate] No token received from Finix");
              toast.error("Card processing failed. Please try again.");
              return;
            }
            
            if (!TOKEN_REGEX.test(tokenId)) {
              console.error("[PublicEstimate] Invalid token format:", tokenId?.substring(0, 10));
              toast.error("Invalid card token. Please try again.");
              return;
            }
            
            // Set processing state
            setProcessingPayment(true);
            
            try {
              // Include fraud_session_id if available (best-effort)
              // If not ready yet, wait up to 300ms for it (fail-open)
              let fraudSessionId = fraudSessionIdRef.current;
              
              if (isDebugSubmit) {
                console.info('[FSP_FRAUD_DEBUG] Pre-submit fraud session from ref:', { length: fraudSessionId?.length || 0 });
              }
              
              // Per Finix docs: merchant_id is required for fraud detection
              if (!fraudSessionId && paymentConfig.merchant_id) {
                // Quick wait for fraud session if still pending
                try {
                  fraudSessionId = await Promise.race([
                    getFinixFraudSessionId({
                      environment: paymentConfig.environment,
                      merchantId: paymentConfig.merchant_id,
                      timeoutMs: 300
                    }),
                    new Promise(resolve => setTimeout(() => resolve(null), 300))
                  ]);
                } catch {
                  // Fail-open: proceed without fraud session
                }
              }
              
              const payload = { token: tokenId };
              if (fraudSessionId && typeof fraudSessionId === "string" && fraudSessionId.trim() !== "") {
                payload.fraud_session_id = fraudSessionId;
              }
              
              if (isDebugSubmit) {
                console.info('[FSP_FRAUD_DEBUG] Final payload keys:', Object.keys(payload));
              }
              
              // Call backend to process payment with validated TK token
              const response = await fetch(`${API}/public/estimate/${publicToken}/pay-deposit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
              });
              
              // Read response body ONCE
              let result;
              try {
                result = await response.json();
              } catch (parseErr) {
                console.error("Failed to parse response:", parseErr);
                toast.error("Invalid server response. Please try again.");
                setProcessingPayment(false);
                return;
              }
              
              // Check for errors after parsing
              if (!response.ok || !result?.success) {
                const errorMsg = result?.error || result?.detail || result?.message || "Payment failed";
                console.error("[PublicEstimate] Payment failed:", errorMsg, result?.diag);
                toast.error(errorMsg);
                setProcessingPayment(false);
                return;
              }
              
              toast.success("Deposit paid successfully!");
              setShowPayDepositDialog(false);
              setProcessingPayment(false);
              
              // Re-fetch estimate to show updated deposit_paid status
              await fetchEstimate();
              
            } catch (err) {
              console.error("Payment processing error:", err);
              toast.error("Payment failed. Please try again.");
              setProcessingPayment(false);
            }
          },
          onError: (error) => {
            console.error("[PublicEstimate] Tokenization error:", error);
            if (mounted) {
              toast.error(error?.message || "Card validation failed");
              setProcessingPayment(false);
            }
          },
          onUpdate: (state, binInfo, hasErrors) => {
            // Form state updated
          }
        });
        
        if (form && mounted) {
          finixFormRef.current = form;
          setFinixReady(true);
        }
      } catch (err) {
        console.error("Error initializing payment form:", err);
        if (mounted) {
          toast.error("Failed to load payment form. Please refresh and try again.");
        }
      }
    };
    
    initFinix();
    
    return () => {
      mounted = false;
      finixFormRef.current = null;
      setFinixReady(false);
    };
  }, [showPayDepositDialog, paymentConfig]);
  
  // Open Pay Deposit dialog - use config from already-loaded estimate
  const handleOpenPayDeposit = () => {
    const isDebug = localStorage.getItem('FSP_FRAUD_DEBUG') === '1';
    
    // Payment config comes from the estimate response, no separate API call needed
    if (!estimate?.payments_enabled || !estimate?.finix_application_id) {
      toast.error("Online payments are not available for this estimate");
      return;
    }
    
    // Robust key selection: try multiple possible key names
    const env = estimate.finix_environment || estimate.environment || "sandbox";
    const applicationId = estimate.finix_application_id || estimate.application_id || null;
    const merchantId = estimate.finix_merchant_id || estimate.merchant_id || null;
    
    if (isDebug) {
      console.info('[FSP_FRAUD_DEBUG] Estimate payment config keys:', {
        hasFinixEnv: !!estimate.finix_environment,
        hasEnv: !!estimate.environment,
        hasFinixAppId: !!estimate.finix_application_id,
        hasAppId: !!estimate.application_id,
        hasMerchantId: !!estimate.finix_merchant_id || !!estimate.merchant_id,
        chosenEnv: env,
        chosenAppId: applicationId ? `***${applicationId.slice(-4)}` : null,
        chosenMerchantId: merchantId ? `***${merchantId.slice(-4)}` : null
      });
    }
    
    // Build payment config from estimate data
    const config = {
      ok: true,
      provider: "finix",
      application_id: applicationId,
      environment: env,
      merchant_id: merchantId,
      deposit_amount: estimate.deposit_amount,
      deposit_amount_cents: estimate.deposit_amount_cents
    };
    
    setPaymentConfig(config);
    setShowPayDepositDialog(true);
  };

  const handleDecline = async () => {
    setDeclining(true);
    try {
      const response = await fetch(`${API}/public/estimate/${publicToken}/decline`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: declineReason })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || "Failed to decline estimate");
        setDeclining(false);
        return;
      }
      
      setDeclined(true);
      setEstimate(prev => ({ 
        ...prev, 
        status: "declined", 
        declined_at: data.declined_at,
        declined_reason: declineReason 
      }));
      setShowDeclineDialog(false);
      setDeclineReason("");
      toast.success("Estimate declined.");
    } catch (err) {
      toast.error("Failed to decline estimate. Please try again.");
    } finally {
      setDeclining(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4" data-testid="public-estimate-loading">
        <div className="text-center">
          <SpinnerGap className="w-10 h-10 animate-spin text-emerald-600 mx-auto" />
          <p className="text-zinc-500 mt-4">Loading estimate...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4" data-testid="public-estimate-error">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-zinc-900 mb-2">Unable to Load Estimate</h1>
            <p className="text-zinc-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const company = estimate?.company || {};

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="public-estimate-page">
      {/* Header with company branding */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {company.logo_url ? (
                <div className="relative">
                  <img 
                    src={company.logo_url} 
                    alt={company.name} 
                    className="h-10 max-w-[150px] object-contain"
                    onError={(e) => {
                      // Hide broken image, show company name instead
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <span 
                    className="text-lg font-semibold text-zinc-900 hidden"
                  >
                    {company.name}
                  </span>
                </div>
              ) : (
                <span className="text-lg font-semibold text-zinc-900">{company.name}</span>
              )}
            </div>
            {/* Show simple indicator only for completed states - no internal status terminology */}
            {accepted && (
              <Badge className="bg-emerald-100 text-emerald-700">
                <CheckCircle className="w-4 h-4 mr-1" />
                Accepted
              </Badge>
            )}
            {declined && (
              <Badge className="bg-red-100 text-red-700">
                <XCircle className="w-4 h-4 mr-1" />
                Declined
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-3xl mx-auto px-4 py-6 pb-32">
        {/* Estimate header */}
        <Card className="mb-4 overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <p className="text-sm text-zinc-500 mb-1">Estimate</p>
                <h1 className="text-2xl font-bold text-zinc-900">
                  #{estimate?.estimate_number}
                </h1>
                {estimate?.title && (
                  <p className="text-zinc-600 mt-1">{estimate.title}</p>
                )}
              </div>
              <div className="text-left sm:text-right">
                <p className="text-sm text-zinc-500">Created</p>
                <p className="font-medium">{formatDate(estimate?.created_at)}</p>
                {estimate?.valid_until && (
                  <p className="text-sm text-zinc-500 mt-2">
                    Valid until: {formatDate(estimate.valid_until)}
                  </p>
                )}
              </div>
            </div>
            
            {estimate?.customer_name && (
              <div className="mt-4 pt-4 border-t border-zinc-100">
                <p className="text-sm text-zinc-500">Prepared for</p>
                <p className="font-medium text-zinc-900">{estimate.customer_name}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Accepted banner */}
        {accepted && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4 flex items-center gap-3" data-testid="estimate-accepted-banner">
            <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-emerald-800">Estimate Accepted</p>
              {estimate?.accepted_at && (
                <p className="text-sm text-emerald-600">
                  Accepted on {formatDate(estimate.accepted_at)}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Deposit Payment Step - shows after acceptance if deposit required but not paid */}
        {accepted && estimate?.deposit_required && !estimate?.deposit_paid && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4" data-testid="deposit-payment-step">
            <div className="flex items-start gap-3">
              <CreditCard className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">Deposit Required</p>
                <p className="text-sm text-amber-700 mt-1">
                  A deposit of <span className="font-semibold">{formatCurrency(estimate.deposit_amount)}</span> is required to proceed.
                </p>
                <Button
                  onClick={handleOpenPayDeposit}
                  disabled={!estimate?.payments_enabled}
                  className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="pay-deposit-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Pay Deposit ({formatCurrency(estimate.deposit_amount)})
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Deposit Paid Confirmation */}
        {accepted && estimate?.deposit_required && estimate?.deposit_paid && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4" data-testid="deposit-paid-banner">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-emerald-800">Deposit Paid</p>
                <p className="text-sm text-emerald-600">
                  {formatCurrency(estimate.deposit_amount)} deposit has been received
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Declined banner */}
        {declined && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-3">
              <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-800">Estimate Declined</p>
                {estimate?.declined_at && (
                  <p className="text-sm text-red-600">
                    Declined on {formatDate(estimate.declined_at)}
                  </p>
                )}
              </div>
            </div>
            {estimate?.declined_reason && (
              <div className="mt-3 pl-9">
                <p className="text-sm text-red-700">
                  <span className="font-medium">Reason:</span> {estimate.declined_reason}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Line items */}
        <Card className="mb-4">
          <CardContent className="p-0">
            <div className="p-4 border-b border-zinc-100">
              <h2 className="font-semibold text-zinc-900 flex items-center gap-2">
                <Receipt className="w-5 h-5 text-zinc-400" />
                Line Items
              </h2>
            </div>
            
            <div className="divide-y divide-zinc-100">
              {estimate?.items?.map((item, idx) => (
                <div key={idx} className="p-4">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-zinc-900">{item.description}</p>
                      <p className="text-sm text-zinc-500 mt-1">
                        {item.quantity} × {formatCurrency(item.price)}
                        {item.is_taxable && <span className="ml-2 text-xs text-zinc-400">(taxable)</span>}
                      </p>
                    </div>
                    <p className="font-medium text-zinc-900 whitespace-nowrap">
                      {formatCurrency(item.quantity * item.price)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Totals */}
        <Card className="mb-4">
          <CardContent className="p-4 space-y-3">
            <div className="flex justify-between text-zinc-600">
              <span>Subtotal</span>
              <span>{formatCurrency(estimate?.subtotal)}</span>
            </div>
            {estimate?.tax > 0 && (
              <div className="flex justify-between text-zinc-600">
                <span>Tax ({estimate?.tax_rate_percent}%)</span>
                <span>{formatCurrency(estimate?.tax)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold text-zinc-900 pt-3 border-t border-zinc-200">
              <span>Total</span>
              <span>{formatCurrency(estimate?.total)}</span>
            </div>
            {/* Show deposit info - different states */}
            {estimate?.deposit_required && (
              <>
                {/* Deposit PAID - show as credit with green styling */}
                {estimate?.deposit_paid ? (
                  <div className="pt-2 border-t border-emerald-200 bg-emerald-50 -mx-4 px-4 py-2 -mb-4 rounded-b-lg space-y-2" data-testid="deposit-info-paid">
                    <div className="flex justify-between text-emerald-700">
                      <span className="flex items-center gap-1">
                        <CreditCard className="w-4 h-4" />
                        Deposit received
                      </span>
                      <span className="font-semibold">-{formatCurrency(estimate?.deposit_amount)}</span>
                    </div>
                    <div className="flex justify-between text-emerald-800 font-bold">
                      <span>Remaining Balance</span>
                      <span>{formatCurrency(estimate?.remaining_due)}</span>
                    </div>
                  </div>
                ) : (
                  /* Deposit NOT paid - show as due with amber styling */
                  <div className="flex justify-between text-amber-700 pt-2 border-t border-amber-200 bg-amber-50 -mx-4 px-4 py-2 -mb-4 rounded-b-lg" data-testid="deposit-info-due">
                    <span className="flex items-center gap-1">
                      <CreditCard className="w-4 h-4" />
                      {accepted ? "Deposit due" : "Deposit due on approval"}
                    </span>
                    <span className="font-semibold">{formatCurrency(estimate?.deposit_amount)}</span>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        {estimate?.notes && (
          <Card className="mb-4">
            <CardContent className="p-4">
              <h3 className="font-semibold text-zinc-900 mb-2">Notes & Terms</h3>
              <p className="text-zinc-600 whitespace-pre-wrap">{estimate.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Company contact info */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <h3 className="font-semibold text-zinc-900 mb-3">Contact Us</h3>
            <div className="space-y-2 text-sm">
              {company.phone && (
                <a href={`tel:${company.phone}`} className="flex items-center gap-2 text-zinc-600 hover:text-emerald-600">
                  <Phone className="w-4 h-4" />
                  {company.phone}
                </a>
              )}
              {company.email && (
                <a href={`mailto:${company.email}`} className="flex items-center gap-2 text-zinc-600 hover:text-emerald-600">
                  <Envelope className="w-4 h-4" />
                  {company.email}
                </a>
              )}
              {company.address && (
                <p className="flex items-center gap-2 text-zinc-600">
                  <MapPin className="w-4 h-4" />
                  {company.address}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Download PDF - opens in new tab to escape sandbox */}
        <Card>
          <CardContent className="p-4">
            <Button
              variant="outline"
              onClick={() => {
                const pdfUrl = `${API}/public/estimate/${publicToken}/pdf`;
                const newWindow = window.open(pdfUrl, "_blank");
                if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                  window.location.href = pdfUrl;
                }
              }}
              className="w-full border-2 py-5"
              data-testid="download-pdf-btn"
            >
              <FilePdf className="w-5 h-5 mr-2" />
              Download PDF
            </Button>
          </CardContent>
        </Card>
      </main>

      {/* Fixed bottom action bar */}
      {estimate?.can_accept && !accepted && !declined && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 p-4 safe-area-pb">
          <div className="max-w-3xl mx-auto flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeclineDialog(true)}
              className="flex-1 border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 py-6 text-lg font-semibold"
              data-testid="decline-estimate-btn"
            >
              <X className="w-5 h-5 mr-2" />
              Decline
            </Button>
            <Button
              onClick={() => setShowAcceptDialog(true)}
              className="flex-[2] bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-semibold"
              data-testid="accept-estimate-btn"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Accept Estimate
            </Button>
          </div>
        </div>
      )}

      {/* Accept Confirmation Dialog */}
      <Dialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Accept Estimate?</DialogTitle>
            <DialogDescription>
              By clicking accept, you are agreeing to the terms and pricing outlined in this estimate.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-zinc-50 rounded-lg p-4 my-4">
            <div className="flex justify-between items-center">
              <span className="text-zinc-600">Estimate Total</span>
              <span className="text-xl font-bold text-zinc-900">
                {formatCurrency(estimate?.total)}
              </span>
            </div>
            {/* Show deposit info in confirmation */}
            {estimate?.deposit_required && (
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-zinc-200" data-testid="accept-dialog-deposit-info">
                <span className="text-amber-700 flex items-center gap-1">
                  <CreditCard className="w-4 h-4" />
                  Deposit required
                </span>
                <span className="font-semibold text-amber-700">
                  {formatCurrency(estimate?.deposit_amount)}
                </span>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAcceptDialog(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAccept}
              disabled={accepting}
              className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-accept-btn"
            >
              {accepting ? (
                <>
                  <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Yes, Accept Estimate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline Confirmation Dialog */}
      <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Decline Estimate?</DialogTitle>
            <DialogDescription>
              Are you sure you want to decline this estimate? The contractor will be notified.
            </DialogDescription>
          </DialogHeader>
          
          <div className="my-4">
            <label className="block text-sm font-medium text-zinc-700 mb-2">
              Reason (optional)
            </label>
            <Textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Let us know why you're declining..."
              className="resize-none"
              rows={3}
              data-testid="decline-reason-input"
            />
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeclineDialog(false);
                setDeclineReason("");
              }}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDecline}
              disabled={declining}
              variant="destructive"
              className="w-full sm:w-auto"
              data-testid="confirm-decline-btn"
            >
              {declining ? (
                <>
                  <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
                  Declining...
                </>
              ) : (
                <>
                  <X className="w-4 h-4 mr-2" />
                  Decline Estimate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Deposit Dialog */}
      <Dialog open={showPayDepositDialog} onOpenChange={(open) => {
        // Only allow closing if not processing
        if (!processingPayment) {
          setShowPayDepositDialog(open);
        }
      }}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => {
          // Prevent closing when clicking outside if processing
          if (processingPayment) e.preventDefault();
        }}>
          <DialogHeader>
            <DialogTitle>Pay Deposit</DialogTitle>
            <DialogDescription>
              Enter your card details to pay the deposit of {formatCurrency(paymentConfig?.deposit_amount)}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="my-4 relative">
            {/* Loading state shown OUTSIDE the Finix container */}
            {!finixReady && (
              <div className="flex items-center justify-center h-[150px] text-zinc-500 border border-zinc-200 rounded-lg">
                <SpinnerGap className="w-6 h-6 animate-spin mr-2" />
                Loading payment form...
              </div>
            )}
            
            {/* Processing state overlay */}
            {processingPayment && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-lg">
                <div className="text-center">
                  <SpinnerGap className="w-8 h-8 animate-spin text-emerald-600 mx-auto" />
                  <p className="mt-2 text-sm text-zinc-600">Processing payment...</p>
                </div>
              </div>
            )}
            
            {/* Finix Payment Form Container - starts empty, Finix populates it */}
            <div 
              id="public-deposit-finix-form"
              className={`min-h-[200px] border border-zinc-200 rounded-lg p-4 ${!finixReady ? 'hidden' : ''}`}
              data-testid="finix-payment-form"
            />
            
            <p className="text-xs text-zinc-500 mt-3 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Secure payment powered by Finix • Click Submit above to pay
            </p>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPayDepositDialog(false)}
              disabled={processingPayment}
              className="w-full"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
